import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store";
import Breadcrumbs from "components/common/Breadcrumbs";
import ContactAssociation from "components/modules/CRM/Contacts/ContactAssociation";
import { useCrmNavigation } from "hooks/useCrmNavigation";
import { useContactOwners } from "service/crm";
import { useContactDetailsByKey } from "service/crm/hooks/useContactDetails";
import { selectAuthUser } from "store/slices/auth/auth.selector";
import ContactDetailsSkeleton from "components/Loaders/ContactDetailsSkeleton";
import { timeAgo } from "utils/date";
import ContactActivity from "components/modules/CRM/Contacts/ContactActivity";
import ContactDetailsCard from "components/modules/CRM/Contacts/ContactDetailsCard";
import ContactEmptyState from "components/modules/CRM/ContactEmptyState";
import { Eye, Plus, Users } from "lucide-react";
import { RecordAccessProvider } from "context/crm/RecordAccessContext";
import { Stat } from "components/common/StatCard";
import { formatUsdAmount } from "utils/helpers";

const ContactDetail = () => {
    const navigate = useNavigate();
    const authUser = useAppSelector(selectAuthUser);
    const { contactKey } = useParams<{ contactKey: string }>();
    const { buildContactsUrl, buildViewContactUrl, buildCreateContactUrl } = useCrmNavigation();
    const { contact, isLoading, isError } = useContactDetailsByKey(
        contactKey ?? "",
    );

    const { data: owners = [] } = useContactOwners(
        authUser?.orgUuid as string,
    );

    const associationTimestamps = [
        contact?.createdAt && {
            label: "Created",
            value: new Date(contact.createdAt).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        },
        contact?.lastActivityAt && {
            label: "Last activity",
            value: timeAgo(contact.lastActivityAt),
        },
    ].filter(Boolean) as { label: string; value: string }[];

    if (isLoading) return <ContactDetailsSkeleton />
    if (isError || !contact) return <ContactEmptyState
        icon={Users}
        title="Contact not found"
        description="The contact you are looking for does not exist or has been deleted."
        action={{
            label: "New Contact",
            icon: Plus,
            onClick: () => navigate(buildCreateContactUrl()),
        }}
        secondaryAction={{
            label: "View Contacts",
            icon: Eye,
            onClick: () => navigate(buildContactsUrl()),
        }}
    />;

    // TODO: remove — debugging the USD deal value from the backend
    console.log("[Contact] dealValue (USD) from API:", contact.dealValue, {
        api: `GET /crm/contacts/${contact.uuid}`,
        deals: contact.deals?.map((d) => ({ name: d.name, amount: d.amount, currency: d.currency })),
    });

    const overdueTasks = contact.overdueTasks ?? 0;

    const stats: Stat[] = [
        {
            label: "Deal value",
            value: contact.dealValue != null ? formatUsdAmount(contact.dealValue) : "—",
            accent: "success",
        },
        {
            label: "Overdue tasks",
            value: overdueTasks,
            // Only flag it when there is something to act on.
            ...(overdueTasks > 0 ? { accent: "warning" as const } : {}),
        },
        { label: "Activities", value: contact.activitiesCount ?? 0 },
        { label: "Notes", value: contact.notesCount ?? 0 },
    ];

    return (
        /**
         * RBAC: resolves this contact's access once — CRM Admin/Manager get full
         * access, a CRM User gets edit access only via `contact.canEdit` (Owner,
         * assigned Task, or assigned Note). Every panel below reads it through
         * `useRecordAccess()`, so none of them re-derive the rule or need it
         * prop-drilled. Mounted below the loading/error guards so `contact` is
         * always present and access never resolves to view-only on first paint.
         */
        <RecordAccessProvider record={contact}>
            <div className='min-h-0 flex-1 flex flex-col text-white'>

                <Breadcrumbs
                    items={[
                        { label: 'Contacts', path: buildContactsUrl() },
                        { label: `${contactKey}`, path: buildViewContactUrl(contactKey as string) },
                    ]}
                    activeLinkProps={{ className: 'text-neutral font-semibold' }}
                    className="px-6 shrink-0"
                />
                {/* ---------- Three column layout ---------- */}
                <div className="flex-1 min-h-0 bg-primary border-t border-primary-subtle mt-3 px-2">
                    <div className="grid grid-cols-1 md:grid-cols-12 items-start h-full min-h-0 lg:grid-rows-[minmax(0,1fr)]">
                        <div className="md:col-span-12 lg:col-span-3 space-y-6 lg:h-full lg:overflow-hidden">
                            <ContactDetailsCard contact={contact} owners={owners} />
                        </div>

                        <div className="md:col-span-8 lg:col-span-6 space-y-6 lg:h-full lg:min-h-0 lg:overflow-y-auto">
                            <ContactActivity contactUuid={contact.uuid} />
                        </div>

                        <div className="md:col-span-12 lg:col-span-3 space-y-6 lg:h-full lg:min-h-0 lg:overflow-y-auto">
                            <ContactAssociation contactUuid={contact.uuid} stats={stats} timestamps={associationTimestamps} contact={contact} />
                        </div>
                    </div>
                </div>
            </div>
        </RecordAccessProvider>
    );
};

export default ContactDetail;